import { Flex, notification, Select } from 'antd';
import { languages } from '../../constants/languages';
import { useMedia } from '../../../hooks/useMediaQuery';
import { useUserService } from '../../../services/userService';
import { notificationType } from '../../constants/notificationType';
import { useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { LANGUAGE_CODES } from '../../constants/languageCodes';
import { isImpersonationTab } from '../../../services/impersonationSession';
import { applyHandoffLanguage, clearHandoffLanguage, getHandoffLanguage } from '../../../utils/handoffLanguage';

export const LanguageSelect = ({ i18n, isUseRequest = true, ariaLabel }) => {
    const { isSmallDevice } = useMedia();
    const { changeLanguage, getUserLanguage } = useUserService();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const savedLang = i18n.resolvedLanguage || i18n.language || LANGUAGE_CODES.ENGLISH;

    const isAuthRoute = location.pathname.startsWith('/auth');
    // the account's language belongs to the impersonated user: while impersonating it is only changed locally
    const isImpersonating = isImpersonationTab();

    const getFallbackLanguage = () => {
        if (isAuthRoute) {
            const urlLang = searchParams.get('lang');
            return urlLang || savedLang;
        }
        return savedLang;
    };

    const handleLanguageChange = async value => {
        try {
            if (isUseRequest && !isImpersonating) {
                await changeLanguage(value);
            }

            // the menu's choice replaces the language the visitor came with, for this visit and on the account
            clearHandoffLanguage();
            i18n.changeLanguage(value);
        } catch {
            notification.open({
                type: notificationType.ERROR,
                message: 'Error',
                description: 'Failed to change language.',
                placement: 'top',
            });
        }
    };

    useEffect(() => {
        // the admin keeps their own language; the account's one is neither read nor overwritten
        if (isImpersonating) {
            return;
        }

        if (!isUseRequest) {
            const initialLang = getFallbackLanguage();

            i18n.changeLanguage(initialLang);

            return;
        }

        // SC-QA-36: the language the visitor came with from the landing wins for this visit, and the account's
        // saved language stays as it is
        const handoffLang = getHandoffLanguage();
        if (handoffLang) {
            applyHandoffLanguage(i18n, handoffLang);
            return;
        }

        const fetchUserLanguage = async () => {
            try {
                const { data } = await getUserLanguage();
                const languageToUse = savedLang && savedLang !== data ? savedLang : data;

                if (!languageToUse) {
                    return;
                }

                if (savedLang && savedLang !== data) {
                    await changeLanguage(savedLang);
                }

                i18n.changeLanguage(languageToUse);
            } catch (error) {
                const fallbackLang = getFallbackLanguage();
                i18n.changeLanguage(fallbackLang);
            }
        };

        fetchUserLanguage();
    }, [isUseRequest, isImpersonating]);

    const currentLanguage = getFallbackLanguage();

    return (
        <Select
            value={currentLanguage}
            onClick={e => e.stopPropagation()}
            onChange={value => handleLanguageChange(value)}
            variant="filled"
            dropdownStyle={{ minWidth: 100, width: 'fit-content' }}
            optionLabelProp="label"
            suffixIcon={null}
            className="language-select"
            aria-label={ariaLabel}
            valueRender={value => {
                const selectedLang = languages[value];

                return (
                    <Flex align="center" gap={8}>
                        <img
                            src={selectedLang.icon}
                            alt={selectedLang.value}
                            width={isSmallDevice ? 32 : 24}
                            height={isSmallDevice ? 32 : 24}
                            style={{ borderRadius: '50%', objectFit: 'cover' }}
                        />
                        {selectedLang.label}
                    </Flex>
                );
            }}
        >
            {Object.values(languages).map(lng => (
                <Select.Option
                    key={lng.value}
                    value={lng.value}
                    label={
                        <Flex align="center" gap={8}>
                            <img
                                src={lng.icon}
                                alt={lng.value}
                                width={isSmallDevice ? 32 : 24}
                                height={isSmallDevice ? 32 : 24}
                                style={{ borderRadius: '50%', objectFit: 'cover' }}
                            />
                            {lng.label}
                        </Flex>
                    }
                >
                    <Flex align="center" gap={8}>
                        <img
                            src={lng.icon}
                            alt={lng.value}
                            width={isSmallDevice ? 32 : 24}
                            height={isSmallDevice ? 32 : 24}
                            style={{ borderRadius: '50%', objectFit: 'cover' }}
                        />
                        {lng.label}
                    </Flex>
                </Select.Option>
            ))}
        </Select>
    );
};
